import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Heart, Shield } from "lucide-react";

interface SuggestionsOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  suggestions: string[];
  onSelectSuggestion: (suggestion: string) => void;
}

const SuggestionsOverlay: React.FC<SuggestionsOverlayProps> = ({
  isOpen,
  onClose,
  suggestions,
  onSelectSuggestion
}) => {
  const handleSelectSuggestion = (suggestion: string) => {
    // Extract text from formatted suggestion if present
    const categoryMatch = suggestion.match(/^\[([^\]]+)\]\s*(.+)$/);
    const text = categoryMatch ? categoryMatch[2] : suggestion;
    onSelectSuggestion(text);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto bg-gradient-to-br from-white to-blue-50/30 border-heaven-gold/20">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-center text-heaven-primary mb-4">
            🎯 Strategic Evangelism Coaching
          </DialogTitle>
          <p className="text-sm text-center text-gray-600 mb-4">
            Enhanced suggestions with Scripture 📖 and counter-worldview arguments ⚖️
          </p>
        </DialogHeader>
        
        <div className="space-y-4">
          {suggestions.map((suggestion, index) => {
            // Extract category and text
            const categoryMatch = suggestion.match(/^\[([^\]]+)\]\s*(.+)$/);
            const text = categoryMatch ? categoryMatch[2] : suggestion;
            
            // Check for strategy indicators
            const hasScripture = text.includes('📖') || suggestion.includes('Scripture') || /\w+\s+\d+:\d+/.test(text);
            const hasCounterWorldview = text.includes('⚖️') || suggestion.includes('counter');
            
            const isOption1 = index === 0;
            const icon = isOption1 ? Heart : Shield;
            const IconComponent = icon;
            
            return (
              <Button
                key={index}
                variant="outline"
                className={`w-full h-auto p-4 text-left whitespace-normal transition-all duration-300 hover:scale-105 ${
                  isOption1 
                    ? 'border-green-300 bg-gradient-to-br from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 text-green-900' 
                    : 'border-orange-300 bg-gradient-to-br from-orange-50 to-red-50 hover:from-orange-100 hover:to-red-100 text-orange-900'
                }`}
                onClick={() => handleSelectSuggestion(suggestion)}
              >
                <div className="w-full space-y-3">
                  <div className="flex items-center gap-2">
                    <IconComponent className={`h-5 w-5 ${isOption1 ? 'text-green-600' : 'text-orange-600'}`} />
                    <span className="text-sm font-bold">
                      Option {index + 1}
                    </span>
                    <div className="flex gap-1 ml-auto">
                      {hasScripture && <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">📖 Scripture</span>}
                      {hasCounterWorldview && <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">⚖️ Counter-Worldview</span>}
                    </div>
                  </div>
                  
                  <div className="text-sm leading-normal">
                    {text}
                  </div>
                </div>
              </Button>
            );
          })}
        </div>
        
        <div className="flex justify-center mt-6">
          <Button 
            variant="ghost" 
            onClick={onClose}
            className="text-heaven-primary hover:bg-heaven-gold/10"
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SuggestionsOverlay;